import { useMemo } from 'react';
import type { Booking, BookingStats, PriceCheck, UserProfile } from '../lib/types';
import { getDevModeScenario, generateDevModeData, getDevModeBannerInfo, type DevModeScenario } from '../lib/devMode';

interface User {
  id: string;
  email: string;
}

interface DevModeState {
  isDevMode: boolean;
  scenario: DevModeScenario;
  bannerInfo: { label: string; counts: string } | null;

  // Auth
  user: User | null;
  session: null;
  authLoading: false;

  // Bookings
  watchingBookings: Booking[];
  historyBookings: Booking[];
  stats: BookingStats;
  bookingsLoading: false;
  lastCheckedAt: string | null;
  archivedCount: 0;
  getPriceHistory: (bookingId: string) => Promise<PriceCheck[]>;
  loadBookings: () => void;
  stopWatching: (bookingId: string) => Promise<boolean>;
  clearArchivedCount: () => void;

  // Profile
  profile: UserProfile | null;
  hasName: boolean;
  loadProfile: () => void;
}

// Detect scenario once at module load
const scenario = getDevModeScenario();

/**
 * Returns mock data matching a URL-defined scenario, or null if not in
 * dev mode. Callers check the return value: if non-null, they use mock
 * data instead of real hooks, enabling UI development without auth or a
 * running backend. The scenario is detected once at module load time so
 * it remains stable across re-renders.
 */
export function useDevMode(): DevModeState | null {
  // Generate data once and memoize
  const devData = useMemo(() => {
    if (!scenario) return null;
    return generateDevModeData(scenario);
  }, []);

  // Not in dev mode - return null so caller knows to use real hooks
  if (!scenario || !devData) {
    return null;
  }

  const bannerInfo = getDevModeBannerInfo(scenario);

  return {
    isDevMode: true,
    scenario,
    bannerInfo,

    // Auth - mock user, no session needed
    user: devData.user,
    session: null,
    authLoading: false,

    // Bookings - all from generated data
    watchingBookings: devData.watchingBookings,
    historyBookings: devData.historyBookings,
    stats: devData.stats,
    bookingsLoading: false,
    lastCheckedAt: devData.lastCheckedAt,
    archivedCount: 0,
    getPriceHistory: async (bookingId: string) => devData.getPriceHistory(bookingId),
    loadBookings: () => {
      console.info('[DevMode] loadBookings called - using static mock data');
    },
    stopWatching: async (bookingId: string) => {
      console.info('[DevMode] stopWatching called for:', bookingId, '- simulating success');
      return true;
    },
    clearArchivedCount: () => {
      console.info('[DevMode] clearArchivedCount called - no-op in dev mode');
    },

    // Profile - mock profile with name set
    profile: devData.profile,
    hasName: true,
    loadProfile: () => {}, // No-op in dev mode
  };
}

// Export for checking dev mode outside of hook context
export { scenario as devModeScenario };
export const isDevMode = scenario !== null;
